/** Tiny i18n: in-memory de/en catalogs, plus useT() hook. */
import { createContext, useContext, useEffect, useMemo, useState } from "react";

export const catalogs = {
  de: {
    nav: {
      dashboard: "Dashboard", research: "Marktforschung", campaigns: "Kampagnen",
      creative: "Kreativ-Studio", assets: "Assets", landing_pages: "Landing Pages",
      content: "Inhalte", integrations: "Integrationen", autopilot: "Autopilot",
      reports: "Reporting", settings: "Einstellungen", assistant: "Assistent",
      approvals: "Freigaben", approval_chains: "Freigabe-Ketten", workflows: "Workflow-Verlauf",
      cockpit: "Marktintelligenz", ad_spend: "Werbeausgaben",
      quality: "Qualität",
      strategy_memory: "Strategie-Gedächtnis",
      knowledge_graph: "Knowledge Graph",
      ad_platforms: "Werbeplattformen",
      discovery_growth: "Discovery & Growth",
      leads: "Anfragen",
      goals: "Ziele",
      home: "Übersicht",
    },
    discovery: {
      title: "Discovery & Growth",
      subtitle: "Video, SMS, SEO, AI-SEO, Wettbewerb, Popups, Ideen und Bild-Varianten — 8 Skills auf einem Panel.",
    },
    cockpit: {
      title: "Marktintelligenz",
      subtitle: "Revenue-Attribution und Funnel-Performance.",
      loading: "Lade Marktintelligenz…",
      error: "Fehler beim Laden des Cockpits",
      period_7d: "Letzte 7 Tage", period_30d: "Letzte 30 Tage", period_90d: "Letzte 90 Tage",
      period_365d: "Letzte 12 Monate",
      kpi_revenue: "Umsatz", kpi_profit: "Bruttogewinn",
      kpi_ad_spend: "Werbeausgaben",
      kpi_leads: "Leads", kpi_sales: "Verkäufe",
      ad_spend_tooltip: "Aggregiert aus Werbeausgaben-Events im Zeitraum.",
      industry_label: "Branche",
      industry_top_quartile: "Top Quartil",
      industry_below: "Unter Branche",
      roas_tooltip_active: "Gesamte Werbeausgabe: {{spend}} im Zeitraum.",
      roas_tooltip_empty: "Keine Werbeausgaben im Zeitraum. CSV hochladen oder Eintrag erfassen.",
      cpl_tooltip_active: "Werbeausgaben {{spend}} / {{leads}} Leads.",
      cpl_tooltip_empty: "Keine Werbeausgaben im Zeitraum.",
      funnel_title: "Lifecycle-Funnel",
      funnel_lead_created: "Leads", funnel_qualified: "Qualifiziert",
      funnel_appointment: "Termine", funnel_order: "Aufträge", funnel_sale: "Verkäufe",
      by_campaign_title: "Pro Kampagne",
      col_campaign: "Kampagne", col_leads: "Leads", col_sales: "Sales",
      col_revenue: "Umsatz", col_profit: "Profit", col_spend: "Ausgabe",
      by_platform_title: "Nach Plattform", by_region_title: "Nach Region",
      no_data: "Keine Daten im gewählten Zeitraum.",
      footnote: "Daten kombinieren Revenue-Events (CRM) und Werbeausgaben-Events. CSV oder API zum Synchronisieren nutzen.",
      go_to_ad_spend: "Werbeausgaben verwalten",
      go_to_integrations: "Integrationen verwalten",
    },
    suggestions: {
      heading: "Empfohlene Aktionen",
      subtitle: "Evidenzbasierte Vorschläge aus aktueller Cockpit-Performance.",
      empty: "Alles im grünen Bereich. Keine Empfehlungen für diesen Zeitraum.",
      apply: "Anwenden", apply_ok: "Autopilot-Aktion erstellt.",
      apply_failed: "Anwenden fehlgeschlagen.", applying: "Wende an…",
      dismiss: "Verwerfen", dismiss_ok: "Empfehlung verworfen.",
      dismiss_failed: "Verwerfen fehlgeschlagen.", dismissing: "Verwerfe…",
      confirm_apply: "Empfehlung anwenden?",
      confirm_apply_body: "Die vorgeschlagene Aktion wird als Autopilot-Aktion angelegt und durchläuft die normalen Genehmigungsschritte.",
      confirm_dismiss: "Empfehlung verwerfen?",
      confirm_dismiss_body: "Diese Empfehlung wird für den aktuellen Zeitraum nicht mehr angezeigt.",
      dismiss_reason_placeholder: "Grund (optional)",
      proposed_action: "Geplante Aktion", target: "Ziel",
      target_platform: "Plattform", target_campaign: "Kampagne",
      confidence: "Konfidenz", more: "weitere", show_less: "Weniger anzeigen",
      autopilot_compat: "Autopilot",
      llm_badge: "KI-generiert", llm_pending: "Aktualisiert…",
      regen: "Neu generieren", regen_ok: "KI-Rationale aktualisiert ✓",
      regen_failed: "Neu-Generierung fehlgeschlagen.",
      impact_lift_suffix: "erwartet",
      impact_savings_suffix: "gespart",
      impact_neutral: "Keine Auswirkung",
      impact_unknown: "Auswirkung unklar",
      sort_label: "Sortieren",
      sort_by_severity: "Nach Schweregrad",
      sort_by_impact: "Nach Auswirkung",
      impact_show_details: "Details anzeigen",
      impact_hide_details: "Details ausblenden",
      impact_llm_loading: "KI-Forecast wird generiert…",
      impact_llm_loading_hint:
        "Erscheint nach kurzer Wartezeit. Heuristik-Wert bleibt unverändert.",
      impact_llm_regen: "Forecast neu generieren",
      impact_llm_regen_ok: "Forecast aktualisiert ✓",
      impact_llm_regen_failed: "Forecast-Aktualisierung fehlgeschlagen.",
      vs_industry: "vs Branche",
    },
    audit: {
      title: "Audit-Verlauf",
      export_csv: "Als CSV exportieren",
      search_placeholder: "Suchbegriff…",
      correlation_placeholder: "correlation_id",
      loading: "Lädt…",
      empty: "Keine Audit-Einträge mit diesen Filtern.",
      load_more: "Weitere laden",
      quick_filters: "Schnellfilter",
      quick_ai_today: "AI-Aktionen heute",
      cross_link: "Audit-Verlauf",
      quick_budget_7d: "Budgetänderungen 7T",
      quick_critical: "Nur kritische",
      severity_info: "info",
      severity_warning: "warnung",
      severity_critical: "kritisch",
    },
    period: {
      custom: "Eigener Zeitraum",
      custom_title: "Eigener Zeitraum wählen",
      apply: "Anwenden",
      from: "Von",
      to: "Bis",
      error_required: "Bitte beide Datumsfelder wählen.",
      error_order: "Startdatum muss vor Enddatum liegen.",
      error_order_strict: "Das Enddatum muss nach dem Startdatum liegen.",
      error_future: "Enddatum darf nicht in der Zukunft liegen.",
    },
    campaign_detail: {
      spend_timeline: "Werbeausgaben über Zeit",
      revenue_events: "Letzte Revenue-Events",
      col_when: "Wann",
      col_kind: "Ereignis",
      col_lead: "Lead",
    },
    connection_detail: {
      summary: "Überblick",
      edit: "Bearbeiten",
      name: "Name",
      status: "Status",
      secrets_actions: "Secrets & Aktionen",
      rotate_oauth: "OAuth-Tokens rotieren",
      rotate_secret: "Webhook-Secret rotieren",
      rotate: "Rotieren",
      test: "Test",
      sync_now: "Jetzt synchronisieren",
      saved: "Gespeichert.",
      save_error: "Speichern fehlgeschlagen.",
      error: "Verbindung nicht gefunden.",
      invalid_json: "Ungültiges JSON in 'config'.",
      token_expired: "abgelaufen",
      refresh_now: "Jetzt aktualisieren",
      refreshing: "Aktualisiere…",
      refresh_ok: "OAuth-Token aktualisiert.",
      refresh_failed: "OAuth-Refresh fehlgeschlagen.",
    },
    notifications: {
      title: "Benachrichtigungen",
      mark_all_read: "Alle als gelesen markieren",
      empty: "Noch keine Benachrichtigungen.",
      tenant_badge: "Tenant",
    },
    ws: { live: "Live", offline: "Offline" },
    ad_spend: {
      title: "Werbeausgaben",
      subtitle: "Erfasse manuelle Einträge oder lade CSV-Exporte hoch, um echte ROAS-/CPL-Werte im Cockpit zu sehen.",
      total_spend: "Gesamt-Ausgabe",
      event_count: "Ereignisse",
      avg_per_event: "Ø pro Ereignis",
      cpl: "CPL", roas: "ROAS",
      action_upload_csv: "CSV hochladen",
      action_manual_entry: "Manueller Eintrag",
      action_download_template: "Vorlage herunterladen",
      filter_period: "Zeitraum",
      filter_platform: "Plattform",
      filter_all_platforms: "Alle Plattformen",
      filter_campaign: "Kampagne",
      filter_all_campaigns: "Alle Kampagnen",
      table_date: "Datum",
      table_campaign: "Kampagne",
      table_platform: "Plattform",
      table_spend: "Ausgabe",
      table_impressions: "Impressionen",
      table_clicks: "Klicks",
      table_conversions: "Konversionen",
      table_source: "Quelle",
      table_notes: "Notizen",
      empty_state: "Noch keine Werbeausgaben erfasst. Lade eine CSV-Datei hoch oder erstelle einen manuellen Eintrag.",
      modal_manual_title: "Werbeausgabe erfassen",
      modal_csv_title: "CSV-Datei hochladen",
      modal_csv_hint: "Pflichtspalten: date, campaign_id, platform, spend_eur. Optional: ad_id, currency, impressions, clicks, conversions.",
      field_date: "Datum",
      field_campaign_id: "Kampagne",
      field_platform: "Plattform",
      field_ad_id: "Ad-ID (optional)",
      field_spend_eur: "Ausgabe (€)",
      field_impressions: "Impressionen",
      field_clicks: "Klicks",
      field_conversions: "Konversionen",
      field_notes: "Notizen",
      submit: "Speichern",
      saved: "Werbeausgabe gespeichert.",
      save_error: "Speichern fehlgeschlagen.",
      upload_success: "{{inserted}} Zeilen importiert, {{skipped}} Duplikate übersprungen, {{rejected}} abgelehnt.",
      upload_error: "Upload fehlgeschlagen.",
      template_downloaded: "Vorlage heruntergeladen.",
      connect_cta: "Werbeplattform verbinden",
      connect_cta_hint: "Live-Synchronisierung mit Meta, Google und LinkedIn folgt in Slice C.",
    },
    gallery: {
      notify_me: "Benachrichtigen",
      notify_success: "Danke! Wir melden uns sobald der Konnektor verfügbar ist.",
      notify_error: "Konnte deine Interesse-Meldung nicht speichern.",
      notify_desc: "Wir benachrichtigen dich, sobald dieser Konnektor live ist. Beschreibe optional deinen Use Case.",
      notify_placeholder: "Wie würdest du diesen Konnektor nutzen? (optional)",
      notify_submit: "Senden",
      status_connected: "Verbunden",
      status_partial: "Teilweise verfügbar",
      status_coming_soon: "Bald verfügbar",
      manage_cta: "Verwalten",
      configure_cta: "Konfigurieren",
    },
    integrations: {
      title: "Integrationen",
      subtitle: "Verbinde dein CRM, deine Ad-Plattformen und Notification-Channels.",
      connected_title: "Verbunden",
      no_connections: "Noch keine Verbindungen — wähle unten einen Konnektor.",
      available_title: "Verfügbare Konnektoren",
      available_subtitle: "Wähle einen Konnektor. Für noch nicht verfügbare CRMs kannst du dich benachrichtigen lassen.",
      loading: "Lade Integrationen…",
    },
    dlq: {
      title: "Dead-Letter-Queue",
      subtitle: "Fehlgeschlagene Webhook-Lieferungen.",
      run_tick: "Tick jetzt ausführen",
      run_tick_success: "Tick ausgeführt: {{delivered}} geliefert, {{failed_retrying}} retry, {{dead}} dead.",
      retrying: "Erneut versuchen",
      toggle_grouped: "Gruppiert",
      toggle_flat: "Flach",
      requeue_all_in_group: "Alle in Gruppe neu starten",
      empty: "Keine fehlgeschlagenen Lieferungen.",
      group_count: "{{count}} Lieferungen",
      first_seen: "Erste Sichtung",
      last_seen: "Letzte Sichtung",
      attempts: "Versuche",
      forbidden_title: "Nur Owner",
      forbidden_message: "Dead-Letter-Queue erfordert die system.admin-Berechtigung.",
    },
    common: { cancel: "Abbrechen", saving: "Speichern…", back: "Zurück" },
    auth: {
      sign_in: "Anmelden", sign_up: "Registrieren", email: "E-Mail", password: "Passwort",
      tenant: "Tenant-Kennung", full_name: "Vollständiger Name", workspace_name: "Workspace-Name",
      log_out: "Abmelden", invalid_credentials: "Ungültige Anmeldedaten",
      welcome: "Willkommen bei", subtitle: "Marketing Operating System",
      no_account: "Noch kein Konto?", has_account: "Bereits Konto?",
    },
    dashboard: { title: "Übersicht", subtitle: "Status deines Marketing-Stacks.",
      empty_kpi: "Daten werden in Phase 3 verfügbar.", recent_runs: "Letzte Agent-Runs", agent: "Agent", status: "Status" },
    research: {
      title: "Marktforschung", subtitle: "Strukturierte, KI-generierte Marktberichte für deine Region.",
      form_goal: "Forschungsziel", form_industry: "Branche", form_region: "Region", form_locale: "Sprache",
      submit: "Bericht erzeugen", placeholder_goal: "z.B. Mehr qualifizierte Käufer-Leads in Wuppertal gewinnen",
      placeholder_industry: "z.B. real_estate", placeholder_region: "z.B. Wuppertal",
      running: "Agent läuft…", success: "Bericht erstellt.", error: "Fehler beim Erstellen",
      market: "Markt", audience: "Zielgruppen", competitors: "Wettbewerber", trends: "Trends",
      ad_angles: "Ad-Angles", objections: "Einwände", triggers: "Trigger",
      risks: "Compliance-Risiken", differentiation: "Differenzierung", summary: "Zusammenfassung",
      confidence: "Konfidenz", mocked_warning: "LLM nicht konfiguriert — Platzhalter-Bericht angezeigt.",
      list_title: "Bisherige Berichte", refresh: "Aktualisieren",
      filter_all: "Alle", filter_succeeded: "Erfolgreich", filter_failed: "Fehlgeschlagen",
      filter_fallback: "Platzhalter",
      empty_title: "Noch keine Marktforschungs-Berichte",
      empty_description: "Reiche oben dein erstes Briefing ein, um deinen ersten Bericht zu generieren.",
      back_to_list: "Zur Übersicht", retry: "Erneut versuchen", retrying: "Wird neu gestartet…",
      retry_success: "Neue Recherche gestartet.", parse_error: "Parsing-Fehler",
      status_succeeded: "Erfolgreich", status_failed: "Fehlgeschlagen", status_fallback: "Platzhalter",
      auto_refresh: "Automatische Aktualisierung läuft…", view: "Öffnen",
    },
    common: { loading: "Lädt…", error: "Fehler", available_phase: "Verfügbar in {phase}", view_roadmap: "Roadmap ansehen", cancel: "Abbrechen", back: "Zurück", refresh: "Aktualisieren" },
    chains: {
      title: "Freigabe-Ketten",
      subtitle: "Mehrstufige Freigaben mit Bedingungen, Eskalation und Vier-Augen-Prinzip.",
      detail_title: "Freigabe-Kette",
      search_placeholder: "Suche nach Subject / Definition…",
      status: "Status", definition: "Definition",
      status_all: "Alle Status", status_pending: "Offen",
      status_approved: "Freigegeben", status_rejected: "Abgelehnt",
      status_cancelled: "Abgebrochen", status_expired: "Abgelaufen",
      definition_all: "Alle Definitionen",
      refresh: "Aktualisieren", loading: "Lädt…",
      empty: "Keine Ketten-Instanzen für diesen Filter.",
      not_found: "Ketten-Instanz nicht gefunden.",
      load_error: "Laden der Kette fehlgeschlagen.",
      col_created: "Erstellt", col_definition: "Definition",
      col_subject: "Subjekt", col_status: "Status", col_step: "Schritt",
      open: "Öffnen", back: "Zurück", current: "aktiv",
      approvers: "Genehmiger", named_users: "benannte Nutzer",
      approve: "Genehmigen", reject: "Ablehnen",
      cancel: "Abbrechen", cancel_btn: "Schließen",
      cancel_title: "Kette abbrechen?",
      cancel_help: "Die Kette wird sofort beendet. Diese Aktion ist nicht umkehrbar.",
      cancel_confirm: "Endgültig abbrechen",
      reject_title: "Ablehnen", reject_help: "Bitte begründen Sie die Ablehnung (mind. 10 Zeichen).",
      reject_placeholder: "Begründung…", reject_confirm: "Ablehnung senden",
      not_eligible: "Sie sind nicht zum Entscheiden in diesem Schritt berechtigt.",
      skipped_reason: "Übersprungen", skip_condition_false: "Bedingung nicht erfüllt",
      snapshot: "Snapshot anzeigen",
      toast_approved: "Freigabe gespeichert.",
      toast_rejected: "Ablehnung gespeichert.",
      toast_cancelled: "Kette abgebrochen.",
      final_note: "Abgeschlossen.",
      see_legacy: "Klassische Freigaben ansehen",
      approval_required_chain: "Kette läuft — Freigabe erforderlich",
    },
    workflows: {
      history: {
        title: "Workflow-Verlauf",
        subtitle: "Letzte Workflow-Läufe Ihres Mandanten mit KPI-Aufschlüsselung.",
        empty: "Noch keine Workflow-Läufe in diesem Zeitraum.",
        not_found: "Workflow-Instanz nicht gefunden.",
        runs_total: "Läufe gesamt",
        input_title: "Eingabe",
        kpi: {
          total_runs: "Läufe (30 T.)",
          success_rate: "Erfolgsquote",
          p95_latency: "p95 Latenz",
          cost: "Kosten",
          window_30d: "30-Tage-Fenster",
        },
        filter: {
          definition: "Definition",
          all_definitions: "Alle Definitionen",
          all: "Alle",
        },
        range: { "7d": "7 Tage", "30d": "30 Tage", "90d": "90 Tage", all: "Gesamt" },
        table: { title: "Läufe" },
        col: {
          created: "Erstellt", definition: "Definition", status: "Status",
          steps: "Schritte", duration: "Dauer", cost: "Kosten",
        },
        perstep: {
          title: "Schritt-Aufschlüsselung",
          subtitle: "Erfolgsquote · p50/p95 Latenz · Ø Kosten je Schritt-ID (Fenster)",
          col: { step: "Schritt", count: "Anzahl", success: "Erfolg",
                 p50: "p50", p95: "p95", mean_cost: "Ø Kosten" },
        },
      },
    },
    assistant: {
      subtitle: "Beschreibe ein Marketingziel. Der KI-Assistent erzeugt einen vollständigen Kampagnenentwurf inkl. Anzeigentexten.",
      goal_label: "Ziel", goal_placeholder: "Beschreiben Sie Ihr Marketingziel…",
      goal_required: "Ziel erforderlich", generate: "Brief erzeugen", generating: "Generiere…",
      step_analyzing: "Marke wird analysiert", step_strategy: "Strategie wird erstellt", step_copy: "Anzeigentexte werden generiert",
      audience: "Zielgruppe", regions: "Regionen", personas: "Personas", products: "Produkte", services: "Services",
      budget_schedule: "Budget & Schedule", offer: "Angebot", tracking: "Tracking", crm_goal: "CRM-Ziel",
      ad_copy: "Anzeigentexte", rationale: "Strategie & Begründung", char_violations: "Zeichenlimit",
      use_this: "Diesen nutzen", edit: "Bearbeiten", discard: "Verwerfen", discard_confirm: "Entwurf verwerfen?",
      discard_help: "Der aktuelle Entwurf wird verworfen.",
      create_campaign: "Kampagne erstellen", materializing: "Erstelle Kampagne…",
      materialized: "Kampagne erstellt.", materialize_failed: "Fehler bei der Erstellung.",
      error_generating: "Fehler beim Generieren.", edits_help: "JSON-Overrides, die in den Vorschlag eingefügt werden.",
      hallucination_remap_chip: "Halluzinierte ID neu zugeordnet",
      requires_manager_title: "Manager-Zugang erforderlich",
      requires_manager: "Der KI-Assistent benötigt Manager-Rechte. Bitte den Admin um eine Rollen-Erweiterung.",
    },
    creative: {
      subtitle: "Generiere plattformspezifische Anzeigentexte pro Framework.",
      platform: "Plattform", framework: "Framework", variants: "Varianten", persona: "Persona",
      from_brief: "Aus Brief-Text", from_campaign: "Aus bestehender Kampagne",
      brief_placeholder: "Kurzes Briefing für die Anzeige…", brief_required: "Brief oder Kampagne erforderlich",
      generate: "Varianten erzeugen", generating: "Generiere…",
      variants_title: "Varianten", copy: "Kopieren", save_as_asset: "Als Asset speichern",
      saved: "Gespeichert", save_failed: "Speichern fehlgeschlagen", copied: "In Zwischenablage kopiert",
      copy_failed: "Kopieren fehlgeschlagen", error_generating: "Fehler beim Generieren.",
      empty_state: "Noch keine Varianten — füllen Sie das Formular aus und drücken Sie Generieren.",
      history: "Verlauf (letzte 10)", history_empty: "Noch keine Copywriting-Runs.",
      requires_role_title: "Zugriff erforderlich",
      requires_role: "Creative Studio benötigt mindestens Producer-Rechte.",
      tabs: { copy: "Texte", images: "Bilder", seo: "SEO" },
      images: {
        prompt_label: "Prompt",
        prompt_placeholder: "Beschreibe das Bild…",
        prompt_required: "Prompt erforderlich.",
        suggest_from_campaign: "Aus Kampagne vorschlagen",
        style_label: "Stil", style_none: "—", style_photo: "Foto",
        style_illustration: "Illustration", style_abstract: "Abstrakt",
        aspect_label: "Seitenverhältnis", n_images_label: "Anzahl",
        platform_label: "Plattform", campaign_label: "Kampagne",
        generate_button: "Bild generieren", generating: "Generiere…",
        empty_state: "Noch keine Bilder — füllen Sie das Formular aus und drücken Sie Generieren.",
        open_in_library: "In Bibliothek öffnen",
        use_in_campaign: "Für Kampagne nutzen",
        attached_toast: "Bild der Kampagne zugewiesen.",
        attach_failed: "Anhängen fehlgeschlagen.",
        saved_to_library_toast: "Bild in der Bibliothek gespeichert.",
        access_required: "Bild-Generierung benötigt mindestens Producer-Rechte.",
        error_generating: "Fehler beim Generieren.",
        alt: "KI-generiertes Bild",
      },
      seo: {
        topic_label: "Thema", topic_placeholder: "Worum geht es auf der Seite?",
        topic_required: "Thema erforderlich.",
        primary_keyword_label: "Haupt-Keyword",
        secondary_keywords_label: "Neben-Keywords",
        region_label: "Region",
        locale_label: "Sprache",
        generate_button: "SEO generieren", generating: "Generiere…",
        empty_state: "Noch kein SEO-Output — füllen Sie das Formular aus und drücken Sie Generieren.",
        result_title: "SEO-Ergebnis",
        meta_title_label: "Meta-Title",
        meta_description_label: "Meta-Description",
        h1_label: "H1",
        h2_options_label: "H2-Optionen (3)",
        keywords_label: "Keywords",
        long_tail_label: "Long-Tail-Vorschläge",
        schema_label: "Schema-Markup",
        outline_label: "Content-Outline",
        copy_button: "Kopieren",
        save_as_asset_button: "Als Asset speichern",
        saved_toast: "SEO als Asset gespeichert.",
        access_required: "SEO-Generierung benötigt mindestens Manager-Rechte.",
        error_generating: "Fehler beim Generieren.",
      },
    },
    provenance: { title: "Asset-Provenance" },
    dashboard_velocity: {
      title: "Bibliotheks-Lerngeschwindigkeit (7T)",
      caption_window: "Letzte 7 Tage",
      kb_promotions: "KB-Promotions", avg_quality: "Ø Qualität",
      top_agents: "Top-Agents",
      empty: "Noch keine KI-Aktivität — generieren Sie Ihren ersten Kampagnen-Brief.",
    },
    reports: {
      subtitle: "Generieren Sie KI-gestützte Marketing-Berichte (6-Fragen-Framework).",
      generate: "Bericht generieren", generating: "Generiere…",
      generate_failed: "Bericht konnte nicht erstellt werden.",
      type: "Typ", locale: "Sprache",
      type_weekly: "Wöchentlich", type_monthly: "Monatlich", type_campaign: "Kampagne",
      type_creative: "Kreativ", type_budget: "Budget", type_regional: "Regional",
      summary_title: "Zusammenfassung", recommended_actions: "Empfohlene Aktionen",
      history: "Bisherige Berichte", history_empty: "Noch keine Berichte generiert.",
      view: "Öffnen", read_only: "Nur-Lese-Zugang. Bericht-Generierung benötigt Manager-Rechte.",
      recap_title: "Wochen-Report",
      recap_subtitle: "Aggregiert KPIs, Top-Kampagnen, Strategy-Memory-Zuwächse und Autopilot-Aktivität.",
      recap_generate: "Report erzeugen", recap_generating: "Erzeuge Report…",
      recap_empty: "Noch nicht erzeugt — Zeitraum wählen und Report anfordern.",
      recap_date_since: "Von", recap_date_until: "Bis",
      recap_download_pdf: "PDF herunterladen", recap_downloading_pdf: "Lade PDF…",
      recap_pdf_unavailable: "PDF-Export in Phase 15 verfügbar (weasyprint fehlt noch).",
      recap_send_email: "Per E-Mail senden",
      email_modal_title: "Report per E-Mail senden",
      email_recipients_label: "Empfänger",
      email_recipients_hint: "Kommagetrennte E-Mail-Adressen.",
      email_send: "Senden", email_sending: "Sende…",
      email_sent: "E-Mail in Ausgangspostfach eingereiht.",
      email_send_failed: "Senden fehlgeschlagen.",
      email_none: "Bitte mindestens eine E-Mail-Adresse angeben.",
      exec_title: "Executive Summary",
      exec_subtitle: "KI-verfasster 3-Absatz-Managementbericht auf Basis des Wochen-Recaps.",
      exec_generate: "Summary erzeugen", exec_generating: "Erzeuge Summary…",
      exec_empty: "Noch keine Summary erzeugt.",
      exec_failed: "Executive-Summary konnte nicht erzeugt werden.",
    },
    landing_pages: {
      list_title: "Landing Pages",
      subtitle: "KI-erzeugte Landing-Page-Entwürfe (Hero + USPs + Social Proof + FAQ + DSGVO).",
      new_draft_button: "+ Neuer Entwurf",
      empty_state: "Noch keine Landing-Page-Entwürfe. Klicken Sie auf „+ Neuer Entwurf“, um Ihren ersten mit dem LP-Agent zu erzeugen.",
      framework: "Framework", confidence: "Konfidenz", updated: "Aktualisiert",
      has_hero_image: "Hero-Bild", linked_campaign: "Kampagne",
      view: "Öffnen", back_to_list: "Zurück zur Liste",
      modal: {
        title: "Neuen LP-Entwurf erzeugen",
        mode_campaign: "Aus bestehender Kampagne",
        mode_brief: "Aus Brief-Text",
        campaign_label: "Kampagne", campaign_placeholder: "Kampagne wählen…",
        brief_label: "Brief",
        brief_placeholder: "Beschreiben Sie das Landing-Page-Briefing…",
        persona_label: "Persona",
        persona_placeholder: "Persona wählen…",
        framework_label: "Framework",
        locale_label: "Sprache",
        submit: "Entwurf erzeugen", submitting: "Generiere…",
        auto_hero: "Hero-Bild automatisch generieren",
        hero_failed_warning: "LP-Entwurf erstellt, aber Hero-Bild fehlgeschlagen — Sie können es manuell generieren.",
        validation_required: "Bitte Kampagne oder Brief-Text angeben.",
        error_generic: "Entwurf konnte nicht erzeugt werden.",
      },
      detail: {
        hero: "Hero", value_props: "Nutzenversprechen",
        social_proof: "Social Proof", quotes: "Stimmen", stats: "Kennzahlen",
        faq: "FAQ", legal: "Rechtliche Hinweise", history: "Versionshistorie",
        hero_image_prompt: "Hero-Bild-Prompt",
        cta_primary: "Primärer CTA", cta_secondary: "Sekundärer CTA",
        imprint_ref: "Impressum-Pfad", privacy_ref: "Datenschutz-Pfad",
        gdpr_consent: "DSGVO-Einwilligungstext",
        history_empty: "Keine vorherigen Versionen.",
        change_summary: "Änderungsnotiz",
      },
      generate_hero_image: "Hero-Bild generieren",
      generating_hero_image: "Generiere Hero-Bild…",
      attached_toast: "Hero-Bild dem Entwurf angehängt.",
      saved_to_library_toast: "Bild in der Bibliothek gespeichert.",
      hero_image_failed: "Bild-Generierung fehlgeschlagen.",
      attach_failed: "Anhängen am Entwurf fehlgeschlagen.",
      view_in_assets: "In Assets öffnen",
      workflow: {
        title: "Workflow-Verlauf",
        subtitle: "Cross-Agent-Kette für diese Landingpage",
        empty: "Diese Landingpage wurde nicht über die LP→Creative-Workflow-Kette erzeugt.",
        instance_label: "Workflow",
        view_run: "Run-Inspektor öffnen",
        steps: {
          draft_lp: "LP-Entwurf",
          persist_lp: "LP persistieren",
          generate_hero: "Hero-Bild generieren",
          persist_assets: "Asset speichern",
          attach_image: "Bild anhängen",
        },
        status: {
          succeeded: "Erfolgreich", failed: "Fehlgeschlagen",
          failed_continue: "Fehlgeschlagen (fortgesetzt)",
          skipped: "Übersprungen", running: "Läuft",
          succeeded_via_fallback: "Erfolgreich (Fallback)",
        },
        skip_reason: {
          condition_false: "Bedingung nicht erfüllt",
          adapter_legacy: "Übersprungen (Legacy)",
          on_giveup_skip: "Übersprungen (Retries erschöpft)",
        },
        attempt_badge: "Versuch {n}/{m}",
      },
    },
    landing_pages_v2: {
      title: "Landing Pages",
      subtitle: "Regionale Landing-Pages, Block-Editor, Live-Preview und Schema.org.",
      new_page: "+ Neue Landing-Page",
      open_programmatic: "Programmatische SEO",
      empty: "Noch keine Landing-Pages. Erstellen Sie eine oder starten Sie einen Programmatic-SEO-Batch.",
      search_ph: "Suche nach Titel, Slug, Region…",
      filter_region: "Region", filter_all: "Alle Regionen",
      filter_status: "Status", filter_all_status: "Alle Status",
      updated: "aktualisiert", open: "Öffnen", back: "Zurück",
      dialog: {
        title: "Neue Landing-Page", template: "Template",
        title_label: "Titel", region: "Region",
        topic: "Topic-Cluster (optional)",
        submit: "Erstellen", tpl_required: "Template wählen.",
        create_failed: "Erstellen fehlgeschlagen.",
      },
      editor: "Block-Editor", preview: "Live-Vorschau",
      field: { title: "Titel", meta: "Meta-Description" },
      benefits_readonly_hint: "Regeneriere via „Copy generieren\" — künftig ist eine Inline-Bearbeitung geplant.",
      generate_copy: "Copy generieren", save: "Speichern",
      publish: "Veröffentlichen", published: "Veröffentlicht.",
      publish_failed: "Veröffentlichen fehlgeschlagen.",
      saved: "Gespeichert.", save_failed: "Speichern fehlgeschlagen.",
      copy_ok: "Copy neu generiert.", copy_fallback: "Template-Fallback verwendet.",
      copy_failed: "Copy-Generierung fehlgeschlagen.",
      load_failed: "Landing-Page nicht gefunden.",
      seo_schema: "SEO / Schema.org",
      schema_types: "Schema-Typen:", no_schema_yet: "Noch nichts generiert.",
      generate_schema: "JSON-LD generieren",
      schema_ok: "Schema.org JSON-LD aktualisiert.",
      schema_failed: "Schema-Generierung fehlgeschlagen.",
      cro_audit: "CRO-Audit", cro_score: "Score:",
      cro_no_recs: "Noch kein Audit ausgeführt. Klicken Sie auf „CRO-Audit starten\".",
      run_cro: "CRO-Audit starten",
      cro_ok: "CRO-Audit aktualisiert.", cro_failed: "CRO-Audit fehlgeschlagen.",
      ab_tests: "A/B-Tests", ab_name: "Name", ab_hypothesis: "Hypothese",
      ab_create: "Anlegen", ab_created: "A/B-Test angelegt.",
      ab_name_required: "Name erforderlich.",
      ab_create_failed: "Anlegen fehlgeschlagen.",
      ab_transition_failed: "Transition fehlgeschlagen.",
      ab_empty: "Noch keine A/B-Tests für diese Landing-Page.",
    },
    programmatic_seo: {
      title: "Programmatische SEO",
      subtitle: "Regionale Landing-Page-Fanouts aus Templates + Skill-Enrichment.",
      back_to_lp: "Zurück zu Landing Pages",
      form_title: "Neuer Batch", template: "Template",
      topic: "Topic-Pattern", regions: "Regionen (eine pro Zeile)",
      regions_hint: "{{n}} Regionen · max. 50 pro Batch",
      run_batch: "Batch starten", generating: "Generiere…",
      batches: "Batches",
      batches_empty: "Noch keine Batches. Starten Sie den ersten links.",
      batch_ok: "Batch abgeschlossen: {{n}} Seiten generiert.",
      batch_failed: "Batch-Erstellung fehlgeschlagen.",
      tpl_required: "Template auswählen.",
      regions_required: "Mindestens eine Region angeben.",
      open: "Öffnen", generated_pages: "Generierte Seiten",
    },
    lifecycle: {
      title: "Lifecycle & Distribution",
      subtitle: "Lead-Magnete, E-Mail-Sequenzen, Analytics-Setup, Loops und Launch-Pläne.",
      tabs: { magnets: "Lead Magnets", sequences: "E-Mail-Sequenzen", analytics: "Analytics-Setup", loops: "Marketing Loops", launches: "Launch-Pläne" },
      magnets: { title: "Lead Magnets", topic_required: "Topic angeben.", generate: "Generieren", generated: "Lead-Magnet erstellt.", gen_failed: "Erstellen fehlgeschlagen.", publish: "Veröffentlichen", published: "Veröffentlicht.", publish_failed: "Publish fehlgeschlagen.", empty: "Noch keine Lead-Magnete." },
      sequences: { title: "E-Mail-Sequenzen", generate: "Generieren", generated: "Sequenz erstellt.", gen_failed: "Erstellen fehlgeschlagen.", open: "Öffnen", simulate: "Simulieren", activate: "Aktivieren", activated: "Aktiviert.", would_send: "E-Mails werden gesendet", empty: "Noch keine Sequenzen." },
      analytics: { title: "Analytics-Setup", generate: "Generieren", generated: "Analytics-Setup erstellt.", gen_failed: "Erstellen fehlgeschlagen.", empty: "Noch kein Setup." },
      loops: { title: "Marketing Loops", generate: "Loop generieren", generated: "Loop erstellt.", gen_failed: "Fehlgeschlagen.", empty: "Noch keine Loops." },
      launches: { title: "Launch-Pläne", generate: "Launch-Plan generieren", date_required: "Datum wählen.", generated: "Launch-Plan erstellt.", gen_failed: "Fehlgeschlagen.", empty: "Noch keine Launch-Pläne." },
    },
    sales_orbit: {
      title: "Sales Orbit",
      subtitle: "RevOps, Angebote, Sales-Kollateral und Prospecting.",
      generate: "Generieren",
      tabs: { offers: "Angebote", collateral: "Sales-Kollateral" },
      revops: { empty: "Noch keine RevOps-Konfigurationen.", generated: "RevOps-Konfiguration erstellt.", gen_failed: "Fehlgeschlagen." },
      offers: { name_required: "Name angeben.", generated: "Angebot erstellt.", gen_failed: "Fehlgeschlagen.", empty: "Noch keine Angebote." },
      collateral: { title_required: "Titel angeben.", generated: "Kollateral erstellt.", gen_failed: "Fehlgeschlagen.", empty: "Noch kein Kollateral." },
      prospecting: { name_required: "Name angeben.", generated: "Liste erstellt.", gen_failed: "Fehlgeschlagen.", empty: "Noch keine Prospect-Listen." },
    },
    settings: { title: "Einstellungen", profile: "Profil", tenant: "Mandant", brand: "Markenprofil", users: "Nutzer", roles: "Rollen & Rechte", api_keys: "API-Schlüssel", flags: "Feature-Flags", audit: "Audit-Log", sessions: "Sitzungen", revoke: "Beenden", language: "Sprache", plan_usage: "Nutzung in diesem Zeitraum", save: "Speichern", saved: "Gespeichert" },
    autopilot_tile: {
      title: "Autopilot-Status",
      empty: "Noch keine Autopilot-Läufe.",
      empty_cta: "Policy konfigurieren →",
      runs_7d: "Läufe (7T)", pending: "Pendant Freigaben",
      last_run: "Letzter Lauf:", open: "Autopilot öffnen →",
      agent_label: "Letzter Agent-Run:",
      agent_chip_disabled_tooltip: "Agent-Run-Inspektor: Klick öffnet die Details.",
    },
    agent_inspector: {
      title: "Agent-Run",
      not_found: "Agent-Run nicht gefunden.",
      load_failed: "Laden fehlgeschlagen.",
      input: "Eingabe", decisions: "Entscheidungen",
      no_decisions: "Keine Entscheidungen aufgezeichnet.",
      output: "Ausgabe", safety: "Safety-Verdikt",
      safety_approved: "Freigegeben", safety_blocked: "Blockiert",
      provenance: "Provenienz", linked_artifacts: "Verknüpfte Artefakte",
      no_artifacts: "Keine verknüpften Artefakte gefunden.",
      rejected: "Verworfen",
    },
    quality: {
      title: "Qualitäts-Cockpit",
      subtitle: "LLM-as-Judge-Bewertung der KI-Outputs der letzten 30 Tage.",
      tenant_avg: "Mandanten-Durchschnitt",
      scored_runs: "Bewertete Runs",
      period_30d: "Letzte 30 Tage",
      per_agent_title: "Pro Agent",
      no_scored_runs: "Noch keine bewerteten Runs. Backfill ausführen oder einige Agenten-Läufe starten.",
      weakest_dist: "Schwächste Dimensionen",
      forbidden: "Keine Berechtigung für Qualitätsdaten.",
      load_failed: "Laden der Qualitätsdaten fehlgeschlagen.",
      card_title: "LLM-Qualitäts-Score",
      empty: "Noch nicht bewertet. Manager können per Re-Judge auslösen.",
      rejudge: "Neu bewerten", rejudging: "Bewerte…",
      rejudge_ok: "Bewertung aktualisiert.",
      rejudge_forbidden: "Nur Manager können neu bewerten.",
      rejudge_timeout: "Zeitüberschreitung – bitte später erneut versuchen.",
      rejudge_failed: "Neu-Bewertung fehlgeschlagen.",
      overall: "Gesamt-Score",
      weakest: "Schwächste Dimension",
      rationale: "Begründung des Judges",
      improvements: "Vorgeschlagene Verbesserungen",
      trend_title: "Trend",
      trend_label: "Tenant-Durchschnitt pro Tag",
    },
    autopilot: {
      page_title: "Autopilot",
      subtitle: "Regeln, Empfehlungen und automatisierte Aktionen für Ihre Kampagnen",
      errors: { no_policy: "Keine aktive Policy gefunden.", load_failed: "Laden fehlgeschlagen.",
                 save_failed: "Speichern fehlgeschlagen.", run_failed: "Autopilot-Lauf fehlgeschlagen." },
      run: { run_now: "Jetzt ausführen", triggering: "Starte…", triggered: "Autopilot-Lauf gestartet." },
      policy: {
        title: "Policy",
        readonly_notice: "Nur Lese-Zugang. Policy-Änderungen benötigen Manager-Rechte.",
        saved: "Policy gespeichert.",
        save: "Policy speichern", saving: "Speichere…",
        active: "Policy aktiv",
        mode_label: "Modus", target_label: "Optimierungsziel",
        budget_rules: "Budget-Regeln", platform_rules: "Plattform-Regeln",
        content_rules: "Inhaltsregeln", schedule: "Zeitplan",
        allowed_platforms: "Erlaubte Plattformen",
        blocked_claims: "Verbotene Aussagen (eine pro Zeile)",
        brand_safety_keywords: "Brand-Safety-Schlüsselwörter (eine pro Zeile)",
        min_quality_score: "Min. Qualitätsscore (0–1)",
        min_data_threshold_days: "Min. Datentage",
        max_daily_change_pct: "Max. Tagesänderung (%)",
        max_daily_change_abs: "Max. Tagesänderung (€)",
        max_monthly_total_abs: "Max. Monatsbudget (€)",
        approval_threshold_pct: "Freigabe-Schwelle (%)",
        approval_threshold_abs: "Freigabe-Schwelle (€)",
        stop_loss_pct: "ROAS-Stop-Loss-Schwelle",
        scaling_step_pct: "Max. Skalierungsschritt (%)",
      },
      modes: {
        off:                  { label: "Aus",                       help: "Engine läuft nicht. Keine Aktionen werden geplant." },
        prepare_only:         { label: "Nur vorbereiten",            help: "VIONA bereitet Aktionen vor; nichts wird ausgeführt." },
        with_approval:        { label: "Mit Freigabe ausführen",     help: "Jede Aktion wird zur Freigabe in die Approvals-Queue gestellt." },
        auto_within_frame:    { label: "Automatik im Rahmen",        help: "Aktionen im gesetzten Budget-/Regelrahmen laufen automatisch, alles darüber braucht Freigabe. Not-Aus jederzeit aktiv." },
      },
      targets: {
        cpl_min:    { label: "CPL minimieren (Default)", help: "Empfohlen für Immobilien-Lead-Gen (BRIMO). CPL ist die einzige zuverlässige Kennzahl bis CRM-Umsatz in Phase 5/6 angebunden ist (ADR-0022)." },
        roas_max:   { label: "ROAS maximieren",          help: "Voraussetzung: echte CRM-Umsatzdaten. Bis dahin nur konzeptionell verfügbar." },
        leads_max:  { label: "Lead-Volumen maximieren",  help: "Maximale Pipeline ohne Kostenkappe." },
        balanced:   { label: "Ausgewogen",               help: "Pareto-Verbesserungen bevorzugen." },
      },
      action_types: {
        pause_campaign: "Kampagne pausieren",
        resume_campaign: "Kampagne fortsetzen",
        generate_optimization_report: "Optimierungs-Bericht erzeugen",
        budget_increase: "Budget erhöhen",
        budget_decrease: "Budget senken",
        duplicate_winning_campaign: "Gewinnerkampagne duplizieren",
        generate_new_creative_variant: "Neue Kreativ-Variante erzeugen",
        request_human_approval: "Manuelle Freigabe anfragen",
      },
      rule_verdicts: {
        mode_gate: "Modus-Gate",
        schedule: "Zeitplan",
        quality_floor: "Qualitätsschwelle",
        budget_pacing: "Budget-Pacing",
        scaling_threshold: "Skalierungslimit",
        roas_stop_loss: "ROAS-Stop-Loss",
        brand_safety_scan: "Brand-Safety-Scan",
      },
      runs: {
        title: "Letzte Läufe", empty: "Noch keine Autopilot-Läufe.", open: "Öffnen",
        n_actions: "{n} Aktionen",
      },
      runs_detail: {
        title: "Autopilot-Lauf", back: "Zurück zur Übersicht",
        not_found: "Lauf nicht gefunden.",
        header: "Lauf-Übersicht", observations: "Beobachtungen", no_observations: "Keine Beobachtungen.",
        selected_actions: "Ausgewählte Aktionen", no_selected: "Keine Aktionen ausgewählt.",
        candidates: "Kandidaten", candidates_show: "{n} Kandidaten anzeigen",
        skipped_actions: "Übersprungene Aktionen",
        target: "Ziel", proposed_change: "Geplante Änderung",
        rule_verdicts: "Regel-Verdikte", execution_result: "Ausführungs-Ergebnis",
        open_approval: "Freigabe öffnen",
        convert_to_approval: "Zur Freigabe wandeln",
        convert_disabled_tooltip: "Nur verfügbar wenn Modus = Freigabe nötig oder Kontrolliert.",
        deterministic: "deterministisch (kein Agent)",
      },
      actions: {
        page_title: "Autopilot-Aktionen", link: "Aktionen",
        subtitle: "Lauf-übergreifende Aktions-Queue mit Freigabe / Ablehnen / Rückgängig.",
        search_placeholder: "Suche nach Typ / Ziel / Begründung…",
        all_states: "Alle Zustände", all_types: "Alle Typen",
        col_created: "Erstellt", col_type: "Typ", col_target: "Ziel", col_state: "Status",
        col_confidence: "Konfidenz", col_rationale: "Begründung",
        empty: "Keine Aktionen passen zum aktuellen Filter.",
        refresh: "Aktualisieren", open_run: "Lauf öffnen",
        approve: "Freigeben", reject: "Ablehnen", rollback: "Rückgängig",
        approve_title: "Aktion freigeben", reject_title: "Aktion ablehnen", rollback_title: "Aktion rückgängig",
        approve_confirm: "Freigeben", reject_confirm: "Ablehnen", rollback_confirm: "Rückgängig",
        approve_success: "Aktion freigegeben.", reject_success: "Aktion abgelehnt.",
        rollback_success: "Aktion zurückgesetzt.", action_failed: "Aktion fehlgeschlagen.",
        comment_placeholder: "Kommentar (optional)…",
        reject_placeholder: "Kommentar erforderlich…",
        comment_required: "Kommentar erforderlich für Ablehnung.",
        rollback_warning: "Rollback ist endgültig und wird die letzte Änderung umkehren.",
        working: "Bitte warten…",
      },
    },
  },
  en: {
    nav: {
      dashboard: "Dashboard", research: "Research", campaigns: "Campaigns",
      creative: "Creative Studio", assets: "Assets", landing_pages: "Landing Pages",
      content: "Content Calendar", integrations: "Integrations", autopilot: "Autopilot",
      reports: "Reports", settings: "Settings", assistant: "AI Assistant",
      approvals: "Approvals", approval_chains: "Approval Chains", workflows: "Workflow History",
      cockpit: "Market Intelligence", ad_spend: "Ad spend",
      quality: "Quality",
      strategy_memory: "Strategy Memory",
      knowledge_graph: "Knowledge Graph",
      discovery_growth: "Discovery & Growth",
      leads: "Requests",
      goals: "Goals",
      home: "Home",
    },
    discovery: {
      title: "Discovery & Growth",
      subtitle: "Video, SMS, SEO, AI-SEO, competitors, popups, ideas and image variants — 8 skills on one panel.",
    },
    cockpit: {
      title: "Market Intelligence",
      subtitle: "Revenue attribution and funnel performance.",
      loading: "Loading market intelligence…",
      error: "Failed to load cockpit",
      period_7d: "Last 7 days", period_30d: "Last 30 days", period_90d: "Last 90 days",
      period_365d: "Last 12 months",
      kpi_revenue: "Revenue", kpi_profit: "Gross profit",
      kpi_ad_spend: "Ad spend",
      kpi_leads: "Leads", kpi_sales: "Sales",
      ad_spend_tooltip: "Aggregated from ad-spend events in the period.",
      industry_label: "Industry",
      industry_top_quartile: "Top quartile",
      industry_below: "Below industry",
      roas_tooltip_active: "Total ad spend: {{spend}} in the period.",
      roas_tooltip_empty: "No ad spend recorded in the period. Upload a CSV or create an entry.",
      cpl_tooltip_active: "Ad spend {{spend}} / {{leads}} leads.",
      cpl_tooltip_empty: "No ad spend recorded in the period.",
      funnel_title: "Lifecycle funnel",
      funnel_lead_created: "Leads", funnel_qualified: "Qualified",
      funnel_appointment: "Appointments", funnel_order: "Orders", funnel_sale: "Sales",
      by_campaign_title: "By campaign",
      col_campaign: "Campaign", col_leads: "Leads", col_sales: "Sales",
      col_revenue: "Revenue", col_profit: "Profit", col_spend: "Spend",
      by_platform_title: "By platform", by_region_title: "By region",
      no_data: "No data in the selected window.",
      footnote: "Data combines revenue events (CRM) and ad-spend events. Use CSV or API to sync.",
      go_to_ad_spend: "Manage ad spend",
      go_to_integrations: "Manage integrations",
    },
    suggestions: {
      heading: "Suggested actions",
      subtitle: "Evidence-based recommendations from current cockpit performance.",
      empty: "All clear. No recommendations for this period.",
      apply: "Apply", apply_ok: "Autopilot action created.",
      apply_failed: "Apply failed.", applying: "Applying…",
      dismiss: "Dismiss", dismiss_ok: "Recommendation dismissed.",
      dismiss_failed: "Dismiss failed.", dismissing: "Dismissing…",
      confirm_apply: "Apply recommendation?",
      confirm_apply_body: "The proposed action will be created as an Autopilot action and go through the normal approval flow.",
      confirm_dismiss: "Dismiss recommendation?",
      confirm_dismiss_body: "This recommendation will be hidden for the current period.",
      dismiss_reason_placeholder: "Reason (optional)",
      proposed_action: "Proposed action", target: "Target",
      target_platform: "Platform", target_campaign: "Campaign",
      confidence: "Confidence", more: "more", show_less: "Show less",
      autopilot_compat: "Autopilot",
      llm_badge: "AI-generated", llm_pending: "Refreshing…",
      regen: "Regenerate", regen_ok: "AI rationale updated ✓",
      regen_failed: "Regeneration failed.",
      impact_lift_suffix: "expected",
      impact_savings_suffix: "saved",
      impact_neutral: "No impact",
      impact_unknown: "Impact unclear",
      sort_label: "Sort",
      sort_by_severity: "By severity",
      sort_by_impact: "By impact",
      impact_show_details: "Show details",
      impact_hide_details: "Hide details",
      impact_llm_loading: "AI forecast loading…",
      impact_llm_loading_hint:
        "Appears shortly. The heuristic number stays unchanged.",
      impact_llm_regen: "Regenerate forecast",
      impact_llm_regen_ok: "Forecast updated ✓",
      impact_llm_regen_failed: "Forecast update failed.",
      vs_industry: "vs industry",
    },
    audit: {
      title: "Audit history",
      export_csv: "Export as CSV",
      search_placeholder: "Search…",
      correlation_placeholder: "correlation_id",
      loading: "Loading…",
      empty: "No audit entries match these filters.",
      load_more: "Load more",
      quick_filters: "Quick filters",
      quick_ai_today: "AI actions today",
      cross_link: "Audit history",
      quick_budget_7d: "Budget changes 7d",
      quick_critical: "Critical only",
      severity_info: "info",
      severity_warning: "warning",
      severity_critical: "critical",
    },
    period: {
      custom: "Custom",
      custom_title: "Choose custom range",
      apply: "Apply",
      from: "From",
      to: "To",
      error_required: "Please choose both dates.",
      error_order: "Start date must be before end date.",
      error_order_strict: "End date must be after start date.",
      error_future: "End date must not be in the future.",
    },
    campaign_detail: {
      spend_timeline: "Ad spend over time",
      revenue_events: "Recent revenue events",
      col_when: "When",
      col_kind: "Event",
      col_lead: "Lead",
    },
    connection_detail: {
      summary: "Summary",
      edit: "Edit",
      name: "Name",
      status: "Status",
      secrets_actions: "Secrets & actions",
      rotate_oauth: "Rotate OAuth tokens",
      rotate_secret: "Rotate webhook secret",
      rotate: "Rotate",
      test: "Test",
      sync_now: "Sync now",
      saved: "Saved.",
      save_error: "Save failed.",
      error: "Connection not found.",
      invalid_json: "Invalid JSON in 'config'.",
      token_expired: "expired",
      refresh_now: "Refresh now",
      refreshing: "Refreshing…",
      refresh_ok: "OAuth token refreshed.",
      refresh_failed: "OAuth refresh failed.",
    },
    notifications: {
      title: "Notifications",
      mark_all_read: "Mark all as read",
      empty: "No notifications yet.",
      tenant_badge: "Tenant",
    },
    ws: { live: "Live", offline: "Offline" },
    ad_spend: {
      title: "Ad spend",
      subtitle: "Record manual entries or upload CSV exports so the cockpit can show real ROAS / CPL.",
      total_spend: "Total spend",
      event_count: "Events",
      avg_per_event: "Avg per event",
      cpl: "CPL", roas: "ROAS",
      action_upload_csv: "Upload CSV",
      action_manual_entry: "Manual entry",
      action_download_template: "Download template",
      filter_period: "Period",
      filter_platform: "Platform",
      filter_all_platforms: "All platforms",
      filter_campaign: "Campaign",
      filter_all_campaigns: "All campaigns",
      table_date: "Date",
      table_campaign: "Campaign",
      table_platform: "Platform",
      table_spend: "Spend",
      table_impressions: "Impressions",
      table_clicks: "Clicks",
      table_conversions: "Conversions",
      table_source: "Source",
      table_notes: "Notes",
      empty_state: "No ad-spend events yet. Upload a CSV or create a manual entry.",
      modal_manual_title: "Record ad spend",
      modal_csv_title: "Upload CSV",
      modal_csv_hint: "Required columns: date, campaign_id, platform, spend_eur. Optional: ad_id, currency, impressions, clicks, conversions.",
      field_date: "Date",
      field_campaign_id: "Campaign",
      field_platform: "Platform",
      field_ad_id: "Ad ID (optional)",
      field_spend_eur: "Spend (€)",
      field_impressions: "Impressions",
      field_clicks: "Clicks",
      field_conversions: "Conversions",
      field_notes: "Notes",
      submit: "Save",
      saved: "Ad-spend entry saved.",
      save_error: "Save failed.",
      upload_success: "{{inserted}} rows imported, {{skipped}} duplicates skipped, {{rejected}} rejected.",
      upload_error: "Upload failed.",
      template_downloaded: "Template downloaded.",
      connect_cta: "Connect an ad platform",
      connect_cta_hint: "Live sync with Meta, Google and LinkedIn lands in Slice C.",
    },
    gallery: {
      notify_me: "Notify me",
      notify_success: "Thanks! We'll let you know as soon as it's available.",
      notify_error: "Could not save your interest signal.",
      notify_desc: "We'll notify you as soon as this connector goes live. Optionally describe your use case.",
      notify_placeholder: "How would you use this connector? (optional)",
      notify_submit: "Send",
      status_connected: "Connected",
      status_partial: "Partial",
      status_coming_soon: "Coming soon",
      manage_cta: "Manage",
      configure_cta: "Configure",
    },
    integrations: {
      title: "Integrations",
      subtitle: "Connect your CRM, ad platforms and notification channels.",
      connected_title: "Connected",
      no_connections: "No connections yet — pick a connector below.",
      available_title: "Available Connectors",
      available_subtitle: "Pick a connector. For CRMs not yet available you can opt in to be notified.",
      loading: "Loading integrations…",
    },
    dlq: {
      title: "Dead-Letter Queue", subtitle: "Failed webhook deliveries.",
      run_tick: "Run tick now",
      run_tick_success: "Tick executed: {{delivered}} delivered, {{failed_retrying}} retry, {{dead}} dead.",
      retrying: "Retrying",
      toggle_grouped: "Grouped", toggle_flat: "Flat",
      requeue_all_in_group: "Requeue all in group",
      empty: "No failed deliveries.",
      group_count: "{{count}} deliveries",
      first_seen: "First seen", last_seen: "Last seen", attempts: "Attempts",
      forbidden_title: "Owner only",
      forbidden_message: "Dead-letter queue requires the system.admin permission.",
    },
    common: { cancel: "Cancel", saving: "Saving…", back: "Back" },
    auth: {
      sign_in: "Sign in", sign_up: "Create account", email: "Email", password: "Password",
      tenant: "Tenant slug", full_name: "Full name", workspace_name: "Workspace name",
      log_out: "Sign out", invalid_credentials: "Invalid credentials",
      welcome: "Welcome to", subtitle: "Marketing Operating System",
      no_account: "No account yet?", has_account: "Already have an account?",
    },
    dashboard: { title: "Overview", subtitle: "Health of your marketing stack.",
      empty_kpi: "Data available in Phase 3.", recent_runs: "Recent agent runs", agent: "Agent", status: "Status" },
    research: {
      title: "Research", subtitle: "Structured, AI-generated reports tailored to your region.",
      form_goal: "Research goal", form_industry: "Industry", form_region: "Region", form_locale: "Language",
      submit: "Generate report", placeholder_goal: "e.g. Win more qualified buyer leads in Wuppertal",
      placeholder_industry: "e.g. real_estate", placeholder_region: "e.g. Wuppertal",
      running: "Agent running…", success: "Report ready.", error: "Failed to generate",
      market: "Market", audience: "Audience", competitors: "Competitors", trends: "Trends",
      ad_angles: "Ad angles", objections: "Objections", triggers: "Triggers",
      risks: "Compliance risks", differentiation: "Differentiation", summary: "Summary",
      confidence: "Confidence", mocked_warning: "LLM not configured — showing placeholder report.",
      list_title: "Previous reports", refresh: "Refresh",
      filter_all: "All", filter_succeeded: "Succeeded", filter_failed: "Failed",
      filter_fallback: "Placeholder",
      empty_title: "No research reports yet",
      empty_description: "Submit your first brief above to generate your first report.",
      back_to_list: "Back to list", retry: "Retry", retrying: "Re-running…",
      retry_success: "New research started.", parse_error: "Parse error",
      status_succeeded: "Succeeded", status_failed: "Failed", status_fallback: "Placeholder",
      auto_refresh: "Auto-refreshing while a run is active…", view: "Open",
    },
    common: { loading: "Loading…", error: "Error", available_phase: "Available in {phase}", view_roadmap: "View roadmap", cancel: "Cancel", back: "Back", refresh: "Refresh" },
    chains: {
      title: "Approval Chains",
      subtitle: "Multi-step approvals with conditions, escalation and two-eyes governance.",
      detail_title: "Approval Chain",
      search_placeholder: "Search subject / definition…",
      status: "Status", definition: "Definition",
      status_all: "All statuses", status_pending: "Pending",
      status_approved: "Approved", status_rejected: "Rejected",
      status_cancelled: "Cancelled", status_expired: "Expired",
      definition_all: "All definitions",
      refresh: "Refresh", loading: "Loading…",
      empty: "No chain instances match the current filter.",
      not_found: "Chain instance not found.",
      load_error: "Failed to load chain.",
      col_created: "Created", col_definition: "Definition",
      col_subject: "Subject", col_status: "Status", col_step: "Step",
      open: "Open", back: "Back", current: "current",
      approvers: "Approvers", named_users: "named users",
      approve: "Approve", reject: "Reject",
      cancel: "Cancel", cancel_btn: "Close",
      cancel_title: "Cancel chain?",
      cancel_help: "The chain will be terminated immediately. This cannot be undone.",
      cancel_confirm: "Cancel for good",
      reject_title: "Reject", reject_help: "Please provide a reason (min. 10 characters).",
      reject_placeholder: "Reason…", reject_confirm: "Submit rejection",
      not_eligible: "You are not eligible to decide on this step.",
      skipped_reason: "Skipped", skip_condition_false: "Condition not met",
      snapshot: "Show snapshot",
      toast_approved: "Approval recorded.",
      toast_rejected: "Rejection recorded.",
      toast_cancelled: "Chain cancelled.",
      final_note: "Closed.",
      see_legacy: "View classic approvals",
      approval_required_chain: "Chain in progress — approval required",
    },
    workflows: {
      history: {
        title: "Workflow History",
        subtitle: "Recent workflow runs for your tenant with KPI breakdowns.",
        empty: "No workflow runs in this window yet.",
        not_found: "Workflow instance not found.",
        runs_total: "runs total",
        input_title: "Input",
        kpi: {
          total_runs: "Runs (30d)",
          success_rate: "Success rate",
          p95_latency: "p95 latency",
          cost: "Cost",
          window_30d: "30-day window",
        },
        filter: {
          definition: "Definition",
          all_definitions: "All definitions",
          all: "All",
        },
        range: { "7d": "7 days", "30d": "30 days", "90d": "90 days", all: "All time" },
        table: { title: "Runs" },
        col: {
          created: "Created", definition: "Definition", status: "Status",
          steps: "Steps", duration: "Duration", cost: "Cost",
        },
        perstep: {
          title: "Per-step breakdown",
          subtitle: "Success rate · p50/p95 latency · mean cost per step-id (window)",
          col: { step: "Step", count: "Count", success: "Success",
                 p50: "p50", p95: "p95", mean_cost: "Mean cost" },
        },
      },
    },
    creative: {
      tabs: { copy: "Copy", images: "Images", seo: "SEO" },
      images: {
        prompt_label: "Prompt",
        prompt_placeholder: "Describe the image…",
        prompt_required: "Prompt required.",
        suggest_from_campaign: "Suggest from campaign",
        style_label: "Style", style_none: "—", style_photo: "Photo",
        style_illustration: "Illustration", style_abstract: "Abstract",
        aspect_label: "Aspect ratio", n_images_label: "Count",
        platform_label: "Platform", campaign_label: "Campaign",
        generate_button: "Generate image", generating: "Generating…",
        empty_state: "No images yet — fill in the form and press Generate.",
        open_in_library: "Open in library",
        use_in_campaign: "Use in campaign",
        attached_toast: "Image attached to campaign.",
        attach_failed: "Could not attach image.",
        saved_to_library_toast: "Image saved to library.",
        access_required: "Image generation requires Producer-level access.",
        error_generating: "Generation failed.",
        alt: "AI-generated image",
      },
      seo: {
        topic_label: "Topic", topic_placeholder: "What is the page about?",
        topic_required: "Topic required.",
        primary_keyword_label: "Primary keyword",
        secondary_keywords_label: "Secondary keywords",
        region_label: "Region",
        locale_label: "Locale",
        generate_button: "Generate SEO", generating: "Generating…",
        empty_state: "No SEO output yet — fill the form and press Generate.",
        result_title: "SEO result",
        meta_title_label: "Meta title",
        meta_description_label: "Meta description",
        h1_label: "H1",
        h2_options_label: "H2 options (3)",
        keywords_label: "Keywords",
        long_tail_label: "Long-tail suggestions",
        schema_label: "Schema markup",
        outline_label: "Content outline",
        copy_button: "Copy",
        save_as_asset_button: "Save as asset",
        saved_toast: "SEO saved as asset.",
        access_required: "SEO generation requires Manager-level access.",
        error_generating: "Generation failed.",
      },
    },
    reports: {
      subtitle: "Generate AI-powered marketing reports (6-question framework).",
      generate: "Generate report", generating: "Generating…",
      generate_failed: "Could not generate report.",
      type: "Type", locale: "Locale",
      type_weekly: "Weekly", type_monthly: "Monthly", type_campaign: "Campaign",
      type_creative: "Creative", type_budget: "Budget", type_regional: "Regional",
      summary_title: "Summary", recommended_actions: "Recommended actions",
      history: "Previous reports", history_empty: "No reports generated yet.",
      view: "Open", read_only: "Read-only access. Generating reports requires manager rights.",
      recap_title: "Weekly recap",
      recap_subtitle: "Aggregates KPIs, top campaigns, Strategy Memory additions and Autopilot activity.",
      recap_generate: "Generate recap", recap_generating: "Generating recap…",
      recap_empty: "Not generated yet — pick a window and request the recap.",
      recap_date_since: "From", recap_date_until: "To",
      recap_download_pdf: "Download PDF", recap_downloading_pdf: "Preparing PDF…",
      recap_pdf_unavailable: "PDF export ships in Phase 15 (weasyprint pending).",
      recap_send_email: "Send by email",
      email_modal_title: "Send recap by email",
      email_recipients_label: "Recipients",
      email_recipients_hint: "Comma-separated email addresses.",
      email_send: "Send", email_sending: "Sending…",
      email_sent: "Email queued in the outbox.",
      email_send_failed: "Send failed.",
      email_none: "Please provide at least one email address.",
      exec_title: "Executive summary",
      exec_subtitle: "AI-written 3-paragraph management narrative built on the weekly recap.",
      exec_generate: "Generate summary", exec_generating: "Generating summary…",
      exec_empty: "No summary generated yet.",
      exec_failed: "Could not generate executive summary.",
    },
    landing_pages: {
      list_title: "Landing Pages",
      subtitle: "AI-drafted landing pages (hero + value props + social proof + FAQ + GDPR).",
      new_draft_button: "+ New draft",
      empty_state: "No landing-page drafts yet. Click + New draft to generate your first one with the LP Agent.",
      framework: "Framework", confidence: "Confidence", updated: "Updated",
      has_hero_image: "Hero image", linked_campaign: "Campaign",
      view: "Open", back_to_list: "Back to list",
      modal: {
        title: "New landing-page draft",
        mode_campaign: "From existing campaign",
        mode_brief: "From brief text",
        campaign_label: "Campaign", campaign_placeholder: "Pick a campaign…",
        brief_label: "Brief",
        brief_placeholder: "Describe the landing-page brief…",
        persona_label: "Persona",
        persona_placeholder: "Pick a persona…",
        framework_label: "Framework",
        locale_label: "Locale",
        submit: "Generate draft", submitting: "Generating…",
        auto_hero: "Auto-generate hero image",
        hero_failed_warning: "LP draft created, but hero image failed — you can generate manually.",
        validation_required: "Provide a campaign or a brief text.",
        error_generic: "Could not generate the draft.",
      },
      detail: {
        hero: "Hero", value_props: "Value props",
        social_proof: "Social proof", quotes: "Quotes", stats: "Stats",
        faq: "FAQ", legal: "Legal blocks", history: "Version history",
        hero_image_prompt: "Hero image prompt",
        cta_primary: "Primary CTA", cta_secondary: "Secondary CTA",
        imprint_ref: "Imprint path", privacy_ref: "Privacy path",
        gdpr_consent: "GDPR consent text",
        history_empty: "No previous versions.",
        change_summary: "Change note",
      },
      generate_hero_image: "Generate hero image",
      generating_hero_image: "Generating hero image…",
      attached_toast: "Hero image attached to draft.",
      saved_to_library_toast: "Image saved to library.",
      hero_image_failed: "Image generation failed.",
      attach_failed: "Could not attach image to draft.",
      view_in_assets: "Open in assets",
      workflow: {
        title: "Workflow timeline",
        subtitle: "Cross-agent chain that produced this landing page",
        empty: "This landing page was not produced via the LP→Creative workflow chain.",
        instance_label: "Workflow",
        view_run: "Open run inspector",
        steps: {
          draft_lp: "Draft LP",
          persist_lp: "Persist LP",
          generate_hero: "Generate hero",
          persist_assets: "Persist assets",
          attach_image: "Attach image",
        },
        status: {
          succeeded: "Succeeded", failed: "Failed",
          failed_continue: "Failed (continued)",
          skipped: "Skipped", running: "Running",
          succeeded_via_fallback: "Succeeded (fallback)",
        },
        skip_reason: {
          condition_false: "Condition not met",
          adapter_legacy: "Skipped (legacy)",
          on_giveup_skip: "Skipped (retries exhausted)",
        },
        attempt_badge: "Attempt {n}/{m}",
      },
    },
    landing_pages_v2: {
      title: "Landing Pages",
      subtitle: "Regional landing pages, block editor, live preview and Schema.org.",
      new_page: "+ New landing page",
      open_programmatic: "Programmatic SEO",
      empty: "No landing pages yet. Create one or start a programmatic-SEO batch.",
      search_ph: "Search by title, slug, region…",
      filter_region: "Region", filter_all: "All regions",
      filter_status: "Status", filter_all_status: "All statuses",
      updated: "updated", open: "Open", back: "Back",
      dialog: {
        title: "New landing page", template: "Template",
        title_label: "Title", region: "Region",
        topic: "Topic cluster (optional)",
        submit: "Create", tpl_required: "Pick a template.",
        create_failed: "Create failed.",
      },
      editor: "Block editor", preview: "Live preview",
      field: { title: "Title", meta: "Meta description" },
      benefits_readonly_hint: "Regenerate via 'Generate copy' — inline editing coming soon.",
      generate_copy: "Generate copy", save: "Save",
      publish: "Publish", published: "Published.",
      publish_failed: "Publish failed.",
      saved: "Saved.", save_failed: "Save failed.",
      copy_ok: "Copy regenerated.", copy_fallback: "Template fallback used.",
      copy_failed: "Copy generation failed.",
      load_failed: "Landing page not found.",
      seo_schema: "SEO / Schema.org",
      schema_types: "Schema types:", no_schema_yet: "Nothing generated yet.",
      generate_schema: "Generate JSON-LD",
      schema_ok: "Schema.org JSON-LD updated.",
      schema_failed: "Schema generation failed.",
      cro_audit: "CRO audit", cro_score: "Score:",
      cro_no_recs: "No audit yet. Click 'Run CRO audit'.",
      run_cro: "Run CRO audit",
      cro_ok: "CRO audit updated.", cro_failed: "CRO audit failed.",
      ab_tests: "A/B tests", ab_name: "Name", ab_hypothesis: "Hypothesis",
      ab_create: "Create", ab_created: "A/B test created.",
      ab_name_required: "Name required.",
      ab_create_failed: "Create failed.",
      ab_transition_failed: "Transition failed.",
      ab_empty: "No A/B tests for this landing page yet.",
    },
    programmatic_seo: {
      title: "Programmatic SEO",
      subtitle: "Regional landing-page fan-outs from templates + skill enrichment.",
      back_to_lp: "Back to landing pages",
      form_title: "New batch", template: "Template",
      topic: "Topic pattern", regions: "Regions (one per line)",
      regions_hint: "{{n}} regions · max 50 per batch",
      run_batch: "Run batch", generating: "Generating…",
      batches: "Batches",
      batches_empty: "No batches yet. Start your first on the left.",
      batch_ok: "Batch complete: {{n}} pages generated.",
      batch_failed: "Batch generation failed.",
      tpl_required: "Pick a template.",
      regions_required: "Provide at least one region.",
      open: "Open", generated_pages: "Generated pages",
    },
    lifecycle: {
      title: "Lifecycle & Distribution",
      subtitle: "Lead magnets, email sequences, analytics setup, loops and launch plans.",
      tabs: { magnets: "Lead Magnets", sequences: "Email Sequences", analytics: "Analytics Setup", loops: "Marketing Loops", launches: "Launch Plans" },
      magnets: { title: "Lead Magnets", topic_required: "Enter a topic.", generate: "Generate", generated: "Lead magnet created.", gen_failed: "Generate failed.", publish: "Publish", published: "Published.", publish_failed: "Publish failed.", empty: "No lead magnets yet." },
      sequences: { title: "Email Sequences", generate: "Generate", generated: "Sequence created.", gen_failed: "Generate failed.", open: "Open", simulate: "Simulate", activate: "Activate", activated: "Activated.", would_send: "emails would be sent", empty: "No sequences yet." },
      analytics: { title: "Analytics Setup", generate: "Generate", generated: "Analytics setup created.", gen_failed: "Generate failed.", empty: "No setup yet." },
      loops: { title: "Marketing Loops", generate: "Generate loop", generated: "Loop created.", gen_failed: "Generate failed.", empty: "No loops yet." },
      launches: { title: "Launch Plans", generate: "Generate launch plan", date_required: "Pick a date.", generated: "Launch plan created.", gen_failed: "Generate failed.", empty: "No launch plans yet." },
    },
    sales_orbit: {
      title: "Sales Orbit",
      subtitle: "RevOps, offers, sales collateral and prospecting.",
      generate: "Generate",
      tabs: { offers: "Offers", collateral: "Sales Collateral" },
      revops: { empty: "No RevOps configurations yet.", generated: "RevOps configuration created.", gen_failed: "Generate failed." },
      offers: { name_required: "Enter a name.", generated: "Offer created.", gen_failed: "Generate failed.", empty: "No offers yet." },
      collateral: { title_required: "Enter a title.", generated: "Collateral created.", gen_failed: "Generate failed.", empty: "No collateral yet." },
      prospecting: { name_required: "Enter a name.", generated: "List created.", gen_failed: "Generate failed.", empty: "No prospect lists yet." },
    },
    dashboard_velocity: {
      title: "Library learning velocity (7d)",
      caption_window: "Last 7 days",
      kb_promotions: "KB promotions", avg_quality: "Avg quality",
      top_agents: "Top agents",
      empty: "No AI activity yet — generate your first campaign brief.",
    },
    settings: { title: "Settings", profile: "Profile", tenant: "Tenant", brand: "Brand profile", users: "Users", roles: "Roles & permissions", api_keys: "API keys", flags: "Feature flags", audit: "Audit log", sessions: "Sessions", revoke: "Revoke", language: "Language", plan_usage: "Usage this period", save: "Save", saved: "Saved" },
    autopilot_tile: {
      title: "Autopilot status",
      empty: "No autopilot runs yet.",
      empty_cta: "Configure policy →",
      runs_7d: "Runs (7d)", pending: "Pending approvals",
      last_run: "Last run:", open: "Open Autopilot →",
      agent_label: "Last agent run:",
      agent_chip_disabled_tooltip: "Agent run inspector — click to view details.",
    },
    agent_inspector: {
      title: "Agent run",
      not_found: "Agent run not found.",
      load_failed: "Failed to load.",
      input: "Input", decisions: "Decisions",
      no_decisions: "No decisions recorded.",
      output: "Output", safety: "Safety verdict",
      safety_approved: "Approved", safety_blocked: "Blocked",
      provenance: "Provenance", linked_artifacts: "Linked artifacts",
      no_artifacts: "No linked artifacts found.",
      rejected: "Rejected",
    },
    quality: {
      title: "Quality cockpit",
      subtitle: "LLM-as-judge ratings of AI outputs across the last 30 days.",
      tenant_avg: "Tenant average",
      scored_runs: "Scored runs",
      period_30d: "Last 30 days",
      per_agent_title: "Per agent",
      no_scored_runs: "No scored runs yet. Run the backfill or launch a few agent runs.",
      weakest_dist: "Weakest dimensions",
      forbidden: "You don't have access to quality data.",
      load_failed: "Failed to load quality data.",
      card_title: "LLM quality score",
      empty: "Not scored yet. Managers can trigger a re-judge.",
      rejudge: "Re-judge", rejudging: "Judging…",
      rejudge_ok: "Score refreshed.",
      rejudge_forbidden: "Only managers can re-judge.",
      rejudge_timeout: "Judge timed out — please retry.",
      rejudge_failed: "Re-judge failed.",
      overall: "Overall score",
      weakest: "Weakest dimension",
      rationale: "Judge rationale",
      improvements: "Suggested improvements",
      trend_title: "Trend",
      trend_label: "Tenant average per day",
    },
    autopilot: {
      page_title: "Autopilot",
      subtitle: "Self-optimizing marketing loop: AI proposes, rules verify, mode decides.",
      errors: { no_policy: "No active policy found.", load_failed: "Failed to load.",
                 save_failed: "Save failed.", run_failed: "Autopilot run failed." },
      run: { run_now: "Run now", triggering: "Starting…", triggered: "Autopilot run started." },
      policy: {
        title: "Policy",
        readonly_notice: "Read-only access. Editing the policy requires manager rights.",
        saved: "Policy saved.",
        save: "Save policy", saving: "Saving…",
        active: "Policy active",
        mode_label: "Mode", target_label: "Optimization target",
        budget_rules: "Budget rules", platform_rules: "Platform rules",
        content_rules: "Content rules", schedule: "Schedule",
        allowed_platforms: "Allowed platforms",
        blocked_claims: "Blocked claims (one per line)",
        brand_safety_keywords: "Brand-safety keywords (one per line)",
        min_quality_score: "Min quality score (0–1)",
        min_data_threshold_days: "Min data days",
        max_daily_change_pct: "Max daily change (%)",
        max_daily_change_abs: "Max daily change (€)",
        max_monthly_total_abs: "Max monthly budget (€)",
        approval_threshold_pct: "Approval threshold (%)",
        approval_threshold_abs: "Approval threshold (€)",
        stop_loss_pct: "ROAS stop-loss floor",
        scaling_step_pct: "Max scaling step (%)",
      },
      modes: {
        off:                  { label: "Off",                   help: "Engine doesn't run. No actions are planned." },
        prepare_only:         { label: "Prepare only",          help: "VIONA prepares actions; nothing is executed." },
        with_approval:        { label: "Execute with approval", help: "Every action is queued in the Approvals page for review." },
        auto_within_frame:    { label: "Automation within frame", help: "Actions inside the budget/rule frame execute automatically; anything beyond waits for approval. Kill-switch always available." },
      },
      targets: {
        cpl_min:    { label: "Minimize CPL (default)",   help: "Recommended for real-estate lead-gen (BRIMO). CPL is the only trustworthy signal until CRM revenue events land in Phase 5/6 (ADR-0022)." },
        roas_max:   { label: "Maximize ROAS",            help: "Requires real CRM revenue data. Conceptual until then." },
        leads_max:  { label: "Maximize lead volume",     help: "Maximum pipeline regardless of cost." },
        balanced:   { label: "Balanced",                 help: "Prefer pareto-dominant improvements." },
      },
      action_types: {
        pause_campaign: "Pause campaign",
        resume_campaign: "Resume campaign",
        generate_optimization_report: "Generate optimization report",
        budget_increase: "Increase budget",
        budget_decrease: "Decrease budget",
        duplicate_winning_campaign: "Duplicate winning campaign",
        generate_new_creative_variant: "Generate creative variant",
        request_human_approval: "Request human approval",
      },
      rule_verdicts: {
        mode_gate: "Mode gate",
        schedule: "Schedule",
        quality_floor: "Quality floor",
        budget_pacing: "Budget pacing",
        scaling_threshold: "Scaling threshold",
        roas_stop_loss: "ROAS stop-loss",
        brand_safety_scan: "Brand-safety scan",
      },
      runs: {
        title: "Recent runs", empty: "No autopilot runs yet.", open: "Open",
        n_actions: "{n} actions",
      },
      runs_detail: {
        title: "Autopilot run", back: "Back to overview",
        not_found: "Run not found.",
        header: "Run overview", observations: "Observations", no_observations: "No observations.",
        selected_actions: "Selected actions", no_selected: "No actions selected.",
        candidates: "Candidates", candidates_show: "Show {n} candidates",
        skipped_actions: "Skipped actions",
        target: "Target", proposed_change: "Proposed change",
        rule_verdicts: "Rule verdicts", execution_result: "Execution result",
        open_approval: "Open approval",
        convert_to_approval: "Convert to approval",
        convert_disabled_tooltip: "Only available when mode = with_approval or auto_within_frame.",
        deterministic: "deterministic (no agent)",
      },
      actions: {
        page_title: "Autopilot actions", link: "Actions",
        subtitle: "Cross-run action queue with approve / reject / rollback.",
        search_placeholder: "Search type / target / rationale…",
        all_states: "All states", all_types: "All types",
        col_created: "Created", col_type: "Type", col_target: "Target", col_state: "State",
        col_confidence: "Confidence", col_rationale: "Rationale",
        empty: "No actions match the current filter.",
        refresh: "Refresh", open_run: "Open run",
        approve: "Approve", reject: "Reject", rollback: "Rollback",
        approve_title: "Approve action", reject_title: "Reject action", rollback_title: "Rollback action",
        approve_confirm: "Approve", reject_confirm: "Reject", rollback_confirm: "Rollback",
        approve_success: "Action approved.", reject_success: "Action rejected.",
        rollback_success: "Action rolled back.", action_failed: "Action failed.",
        comment_placeholder: "Comment (optional)…",
        reject_placeholder: "Comment required…",
        comment_required: "A comment is required to reject.",
        rollback_warning: "Rollback is final and will reverse the last change.",
        escalate: "Escalate",
      escalate_title: "Escalate to approval",
      escalate_confirm: "Escalate",
      escalate_success: "Action escalated to approval.",
      working: "Working…",
      },
    },
  },
};

const LocaleCtx = createContext({ locale: "de", setLocale: () => {} });

export function LocaleProvider({ children }) {
  const [locale, setLocaleState] = useState(localStorage.getItem("viona.locale") || "de");
  const setLocale = (l) => {
    localStorage.setItem("viona.locale", l);
    setLocaleState(l);
  };
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);
  const value = useMemo(() => ({ locale, setLocale }), [locale]);
  return <LocaleCtx.Provider value={value}>{children}</LocaleCtx.Provider>;
}

export function useLocale() {
  return useContext(LocaleCtx);
}

export function useT() {
  const { locale } = useLocale();
  return (key, vars) => {
    const parts = key.split(".");
    let cur = catalogs[locale] || catalogs.de;
    for (const p of parts) {
      if (cur && typeof cur === "object" && p in cur) cur = cur[p];
      else return key;
    }
    if (typeof cur === "string" && vars) {
      return cur.replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? String(vars[k]) : `{${k}}`));
    }
    return typeof cur === "string" ? cur : key;
  };
}
